<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import countryPhoneCodes from '@/common/countryPhoneCodes.json';
import InputEmail from '@/components/session/ui/InputEmail.vue';
import InputPhoneWithDropdown from '@/components/session/ui/InputPhoneWithDropdown.vue';
import { ContactChannels } from '@/models/ContactChannels';
import { useKycSessionStore } from '@/stores/kycSession.store';
import Mixpanel from '@/integrations/Mixpanel';
import * as Sentry from '@sentry/vue';
import { redirectToTimeoutWithSessionId } from '@/util/timeoutRedirector';
import { useRouter } from 'vue-router';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

const isLoading = ref(true);

const { kycSession } = storeToRefs(useKycSessionStore());
const router = useRouter();

if (!kycSession.value) {
  throw new Error('Kyc session not found');
}
const { cardholder, region } = kycSession.value;

interface ContactChannelsForm {
  email: string;
  phonePrefix: { name: string; dialCode: string; icon: string; code: string };
  phoneNumber: string;
  errorMessages: { email?: string; phoneNumber?: string };
}

const form = reactive<ContactChannelsForm>({
  email: '',
  phonePrefix: { name: '', dialCode: '', icon: '', code: '' },
  phoneNumber: '',
  errorMessages: {},
});

function setPhoneNumberError(isError: boolean) {
  form.errorMessages.phoneNumber = isError ? 'Invalid phone number' : undefined;
}

function setEmailError(isError: boolean) {
  form.errorMessages.email = isError ? 'Invalid email' : undefined;
}

async function validateAndSaveContactChannels() {
  isLoading.value = true;

  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }

  const isValidPhoneNumber = ContactChannels.validatePhoneNumber({
    phoneNumber: form.phoneNumber,
    phonePrefix: form.phonePrefix.dialCode,
  });

  if (!isValidPhoneNumber) {
    setPhoneNumberError(true);
    isLoading.value = false;
    return false;
  }

  if (!ContactChannels.isValidEmail(form.email)) {
    setEmailError(true);
  }

  if (form.errorMessages.email) {
    isLoading.value = false;
    return false;
  }

  const newContactChannels = ContactChannels.create({
    email: { emailAddress: form.email },
    phone: { number: form.phoneNumber, prefix: form.phonePrefix.dialCode },
  });

  try {
    await kycSession.value.cardholder.updateContactChannels(newContactChannels);
  } catch (e) {
    Sentry.captureException(e);
    await router.push(redirectToTimeoutWithSessionId({ kycSession: kycSession.value }));
    return false;
  }

  return true;
}

// Used for EmailEntered event tracking
let loadedEmail = '';
let loadedPhoneNumber = '';

async function loadFormData() {
  if (!cardholder) {
    throw new Error('Cardholder not found');
  }

  //TEMPORARY: Not autopupulating contact channels for BGW users
  if (kycSession.value?.skipAutoPopulation()) {
    const phonePrefixDetails = countryPhoneCodes.find(
      (country) => country.code === region?.toUpperCase()
    );

    if (phonePrefixDetails) {
      form.phonePrefix = phonePrefixDetails;
    }

    return;
  }
  const contactChannels = await cardholder.loadContactChannels();
  const email = contactChannels?.email.emailAddress ?? '';
  const phoneNumber = contactChannels?.phone.number ?? '';
  loadedEmail = email;
  loadedPhoneNumber = phoneNumber;
  form.email = email;
  form.phoneNumber = phoneNumber;

  const phonePrefix = contactChannels?.phone.prefix;

  const phonePrefixDetails = phonePrefix
    ? countryPhoneCodes.find((country) => country.dialCode === phonePrefix)
    : countryPhoneCodes.find((country) => country.code === region?.toUpperCase());

  if (phonePrefixDetails) {
    form.phonePrefix = phonePrefixDetails;
  }
}

const isNextButtonDisabled = computed(
  () =>
    !(
      form.phoneNumber &&
      form.phonePrefix.dialCode &&
      form.email &&
      !form.errorMessages.email &&
      !form.errorMessages.phoneNumber &&
      !isLoading.value
    )
);

onBeforeMount(async () => {
  await loadFormData();
});

function onEmailBlur() {
  if (form.email === loadedEmail) {
    return;
  }
  Mixpanel.instance.trackEmailEntered();
}

function onPrefixSelected() {
  Mixpanel.instance.trackAreaCodeChanged();
}

function onPhoneBlur() {
  if (form.phoneNumber === loadedPhoneNumber) {
    return;
  }
  Mixpanel.instance.trackPhoneNumberEntered();
}

isLoading.value = false;
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col" data-test="screen-kyc-contacts">
      <h1 class="mb-4 text-3xl">What are your contact details?</h1>
      <p class="mb-4">
        This information is used to contact you about the status of your card application and is
        required for enabling Apple Pay, Google Pay, and 3D Secure.
      </p>
      <!-- Email -->
      <form aria-label="contact details">
        <div>
          <label for="email-input" class="text-charcoal-800 text-base font-bold"
            >Email address</label
          >
          <InputEmail
            class="mt-2"
            :class="{
              'border-utility-red': form.errorMessages,
            }"
            v-model="form.email"
            :disabled="isLoading"
            :setEmailError="setEmailError"
            :isError="!!form.errorMessages.email"
            @blur="onEmailBlur"
          />
          <p
            v-if="form.errorMessages.email"
            class="text-light-utility-red dark:text-dark-utility-red mt-3 text-sm"
            data-test="email-validation-error"
          >
            {{ form.errorMessages.email }}
          </p>
        </div>

        <!-- Phone -->
        <div class="mt-6 mb-10">
          <label for="phone-number-input" class="text-charcoal-800 text-base font-bold"
            >Mobile phone number</label
          >
          <InputPhoneWithDropdown
            class="mt-2"
            :dropdownOptions="countryPhoneCodes"
            v-model:phonePrefix="form.phonePrefix"
            v-model:phoneNumber="form.phoneNumber"
            :disabled="isLoading"
            :setPhoneNumberError="setPhoneNumberError"
            :isError="!!form.errorMessages.phoneNumber"
            @prefix-selected="onPrefixSelected"
            @blur="onPhoneBlur"
          />
          <p
            v-if="form.errorMessages.phoneNumber"
            class="text-light-utility-red dark:text-dark-utility-red mt-3 text-sm"
            data-test="phoneNumber-validation-error"
          >
            {{ form.errorMessages.phoneNumber }}
          </p>
        </div>
      </form>
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
      <FlowNavigationButton
        text="Next"
        direction="forward"
        :preNavigationAction="validateAndSaveContactChannels"
        :disabled="isNextButtonDisabled"
      />
    </template>
  </KycSessionLayout>
</template>
